import produce from "immer";

export interface SavedQuery {
  ID: string;
  NAME: string;
  QUERY: string;
  TABLE_STATE?: string | null;
  TAGS?: string | null;
  USER_ID: string;
  SAVE_TYPE: string;
  LAST_SAVED: string;
  USER_NAME: string;
  IS_OWNER: boolean;
  DESCRIPTION: string;
  /**
   * The source element 0 names (decision DQ): the query runs on it for every
   * user. Null when it resolves by season; absent from pre-E6 APIs.
   */
  pinnedSource?: ExplorerSource | null;
}

interface QueryFormBase {
  queryName: string;
  description: string;
  saveType: 'public' | 'private' | 'archived';
  tags?: string[];
}

export type SavedQueryForm = QueryFormBase & {
  overwrite?: "new" | "overwrite";
}

export type EditQueryForm = QueryFormBase & {
  editQuery?: string;
  editQueryName?: string;
  editQueryId: string;
}

export interface MetricGroup {
  /**
   * Metric groups only (not element 0): prefixes this group's result columns, so
   * "keep" turns iAST% into keep_iAST%. The backend keeps word characters,
   * hyphens and spaces; two groups with the same name still collide (_x / _y).
   */
  name?: string;
  metrics?: Metric[];
  dimensions: Dimension[];
  filters: Dimension[];
  ratio: Metric | null;
  /** Metric to calculate "% of" within primary dimension groups (element 0 only) */
  dimensionRatio?: Metric | null;
  /** When true, add "Overall" rows that aggregate across secondary dimensions */
  includeOverall?: boolean;
  /**
   * Element 0 only. Written by `ExplorerAPI` from the page on every request;
   * a value stored in a saved query is overwritten, never trusted.
   */
  scope?: ExplorerScope;
  /**
   * Element 0 only: force one provider for every season (dev source picker).
   * Absent or null, each season resolves to its default source.
   */
  source?: ExplorerSource | null;
}

// ============================================================================
// Sources, scopes and the query context (CTG migration, ENGINE_NOTES §a)
// ============================================================================

/** The frontend surface a request belongs to: NBA pages vs draft pages. */
export type ExplorerScope = "nba" | "draft";

/** A data provider: SecondSpectrum and Cleaning The Glass on nba, SkillCorner on draft. */
export type ExplorerSource = "ss" | "ctg" | "skill";

/** How the backend resolved a query's seasons to sources. */
export interface QueryResolution {
  scope: ExplorerScope;
  sources: ExplorerSource[];
  seasonsBySource: Partial<Record<ExplorerSource, number[]>>;
  explicit: boolean;
  notes?: string[];
}

export interface ContextFilter {
  name: string;
  op: string;
  values: any[];
}

/**
 * exec_query's `queryContext`: the global filters plus the scope and sources the
 * query resolved to. Lazy cells (charts, video) forward `scope` / `source` so they
 * read the same provider as the query that produced them.
 */
export interface ExplorerQueryContext {
  filters: ContextFilter[];
  scope?: ExplorerScope | null;
  source?: ExplorerSource | null;
  resolution?: QueryResolution;
}

/**
 * One saved-query value the engine translated at bind time (exec_query
 * `recoded`, F1 contract §7a):
 *  - `alias`: the same meaning under the core label (`regular season` → `regular`)
 *  - `changed`: the meaning moved (`soft` → `drop`)
 *  - `id`: an SS uuid bound as the same player / team / game's core id
 *  - `unmapped`: a saved id with no core twin; it matches nothing
 */
export interface RecodedValue {
  filter: string;
  kind: "alias" | "changed" | "id" | "unmapped";
  from: string;
  to: string[];
}

/** exec_query `seams_crossed`: item name → seam ids, only for multi-source queries. */
export type SeamsCrossed = Record<string, string[]>;

/**
 * GET /api/current_season (F1 contract §7a). Start years: `season` turns over
 * at the scope's first regular-season game, `seasonAny` at its first game of
 * any type (preseason included).
 */
export interface CurrentSeason {
  scope: ExplorerScope;
  season: number;
  seasonAny: number;
}

/** One entry of GET /explorer/api/seams. */
export interface SeamInfo {
  seam: string;
  title: string;
  summary: string;
  firstSeasonCtg?: number | null;
  items?: string[];
}

interface MetricGroupPreset {
  label: string;
  metrics: Metric[];
}

export type Metric = {
  index?: number;
  value: string;
  label: string;
};

export type DimensionType = "dimensions" | "filters";

export type FilterResponse = {
  [filterName: string]: FilterOption;
};

export interface Dimension {
  name?: Metric;
  op?: Operation;
  values?: Value[] | number | string;
  params?: number;
  /** When true, this dimension is a primary grouping dimension for ratio calculations */
  primary?: boolean;
}

/** Lifecycle status from the backend registry (lib/registry_status.py). */
export type FilterStatus = "active" | "rare" | "deprecated" | "unavailable";

/** Statuses still offered in the query builder. Others resolve but aren't listed. */
export const OFFERED_STATUSES: FilterStatus[] = ["active", "rare"];

export function isOffered(option?: FilterOption): boolean {
  return OFFERED_STATUSES.includes(option?.status ?? "active");
}

export interface FilterOption {
  ops: Operation[];
  type: "values" | "number" | "array_uuid_bd" | "date";
  values: Value[];
  player_id?: boolean;
  /** Absent on older cached payloads, which are treated as active. */
  status?: FilterStatus;
  statusReason?: string;
  /** Set when the filter is only populated for some seasons, e.g. "populated in 2018,2021,2022 only". */
  seasonCoverage?: string;
}

export interface PlayerFilterOptionsResponse {
  values: Value[];
}

export type Operation = {
  label: string;
  value: string;
};

export type Value = {
  label: string;
  value: string;
};

export interface ExplorerError {
  message: string;
  errorType?: string;
  /** The 422 body beyond `error` / `errorType` (item, itemSources, seasonsBySource, …). */
  details?: Record<string, any>;
}

/* ******************* */
/* BLANKS AND DEFAULTS */
/* ******************* */

export const blankDimension = {
  name: undefined,
  op: undefined,
  values: undefined,
  params: 0,
};

/**
 * The regular season as the scope's SeasonType picker names it: core's
 * `regular` on nba (labelled "regular season"), SkillCorner's own label on draft.
 */
export function regularSeasonValue(scope: ExplorerScope): Value {
  return scope === "nba"
    ? { value: "regular", label: "regular season" }
    : { value: "regular season", label: "regular season" };
}

/** A new Explorer query: every team, `season`'s regular season, no garbage time. */
export function defaultQueryGroup(
  scope: ExplorerScope,
  seasonYear: number,
): MetricGroup {
  const season = String(seasonYear);
  return {
    dimensions: [
      {
        name: { value: "TeamOffense", label: "TeamOffense" },
        op: { label: "in", value: "in" },
      },
    ],
    dimensionRatio: null,
    filters: [
      {
        name: { value: "Season", label: "Season" },
        op: { label: "in", value: "in" },
        values: [{ label: season, value: season }],
      },
      {
        name: { value: "SeasonType", label: "SeasonType" },
        op: { label: "in", value: "in" },
        values: [regularSeasonValue(scope)],
      },
      {
        name: { value: "Garbage", label: "Garbage" },
        op: { label: "in", value: "in" },
        values: [{ label: "False", value: "False" }],
      },
    ],
    ratio: null,
  };
}

const possMetric = { value: "Poss", label: "Poss" };

const efficiencyMetrics: MetricGroupPreset = {
  label: "Efficiency",
  metrics: [
    { value: "aPPP", label: "aPPP" },
    { value: "PPP", label: "PPP" },
    { value: "ePPP", label: "ePPP" },
  ],
};

const per100EfficiencyMetrics = {
  label: "per 100",
  metrics: [
    { value: "aPP100", label: "aPP100" },
    { value: "PP100", label: "PP100" },
    { value: "ePP100", label: "ePP100" },
  ],
};

const fourFactorsMetrics = {
  label: "Four Factors",
  metrics: [
    { value: "TS%", label: "TS%" },
    { value: "ORB%", label: "ORB%" },
    { value: "TOV%", label: "TOV%" },
    { value: "FTRate", label: "FTRate" },
  ],
};

const shotProfileMetrics = {
  label: "Shot Profile",
  metrics: [
    { value: "RimRate", label: "RimRate" },
    { value: "MidRate", label: "MidRate" },
    { value: "3Rate", label: "3Rate" },
    { value: "RimTS%", label: "RimTS%" },
    { value: "MidTS%", label: "MidTS%" },
    { value: "3TS%", label: "3TS%" },
  ],
};

const individualOffensiveMetrics = {
  label: "Ind. Offense",
  metrics: [
    { value: "iUSG%", label: "iUSG%" },
    { value: "iAST%", label: "iAST%" },
    { value: "iTOV%", label: "iTOV%" },
    { value: "iAST/TOV", label: "iAST/TOV" },
    { value: "iORB%", label: "iORB%" },
    { value: "iTS%", label: "iTS%" },
    { value: "iFG%", label: "iFG%" },
    { value: "iFT%", label: "iFT%" },
    { value: "iFTRate", label: "iFTRate" },
  ],
}

const individualShotProfile = {
  label: "Ind. Shot Profile",
  metrics: [
    { value: "iRimRate", label: "iRimRate" },
    { value: "iMidRate", label: "iMidRate" },
    { value: "i3Rate", label: "i3Rate" },
    { value: "iRimFG%", label: "iRimFG%" },
    { value: "iMidFG%", label: "iMidFG%" },
    { value: "i3FG%", label: "i3FG%" },
  ],
}

const individualDefensiveMetrics = {
  label: "Ind. Defense",
  metrics: [
    { value: "iDRB%", label: "iDRB%" },
    { value: "iSTL%", label: "iSTL%" },
    { value: "iBLK%", label: "iBLK%" },
    { value: "iFoulRate", label: "iFoulRate" },
  ],
}

export const metricGroupPresets = [
  efficiencyMetrics,
  per100EfficiencyMetrics,
  fourFactorsMetrics,
  shotProfileMetrics,
  individualOffensiveMetrics,
  individualShotProfile,
  individualDefensiveMetrics,
];

export const blankMetricGroup = {
  metrics: [possMetric],
  dimensions: [{ params: 0 }],
  filters: [{ params: 0 }],
  ratio: null,
};

export const defaultMetricGroup = {
  dimensions: [{ params: 0 }],
  filters: [{ params: 0 }],
  ratio: null,
  metrics: [
    { value: "Video", label: "Video" },
    { value: "ShotChart", label: "ShotChart" },
    possMetric,
    ...efficiencyMetrics.metrics,
    ...fourFactorsMetrics.metrics,
    ...shotProfileMetrics.metrics,
  ],
};

export const sortQuery = produce((draft: MetricGroup[]) => {
  draft.forEach((mg, i) => {
    draft.splice(i, 1, sortMetricGroup(mg))
  })
})

const sortMetricGroup = produce((draft: MetricGroup) => {
  if (draft.metrics && Array.isArray(draft.metrics)) {
    draft.metrics.sort((a, b) => a.value > b.value ? 1 : -1)
  }

  const DEFAULT_FILTERS = ["Season", "SeasonType", "Garbage"];
  const dimensionSort = (a: Dimension, b: Dimension) => {
    const aDefault = DEFAULT_FILTERS.includes(a.name?.value ?? "");
    const bDefault = DEFAULT_FILTERS.includes(b.name?.value ?? "");
    if (aDefault && bDefault) return 0;
    if (aDefault) return -1;
    if (bDefault) return 1;
    return (a.name?.value ?? "") > (b.name?.value ?? "") ? 1 : -1
  }

  draft.filters.sort(dimensionSort)
  draft.dimensions.sort(dimensionSort)
})
